import { FC } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { VerificationFileStatus } from 'entities/VerificationPhoto';

import { ReactComponent as CheckIcon } from 'shared/assets/icons/CheckMark.svg';
import { ReactComponent as RejectIcon } from 'shared/assets/icons/CancelIcon.svg';
import { ReactComponent as ClockIcon } from 'shared/assets/icons/ClockIcon.svg';

const getStatusClass = (
  status?: VerificationFileStatus,
): {
  borderClass: string;
  badgeColor: string;
  badgeIcon: React.ReactNode;
  badgeText: string;
  borderStyle: string;
} => {
  switch (status) {
    case VerificationFileStatus.APPROVED:
      return {
        borderClass: 'border-success text-success',
        badgeColor: 'bg-success text-white',
        badgeIcon: <CheckIcon className="[&>path]:fill-white mr-2 w-[16px] h-[16px]" />,
        badgeText: 'Approved',
        borderStyle: 'border-solid',
      };

    case VerificationFileStatus.REJECTED:
      return {
        borderClass: 'border-error text-error',
        badgeColor: 'bg-error text-white',
        badgeIcon: <RejectIcon className="[&>path]:stroke-white mr-2 w-[16px] h-[16px]" />,
        badgeText: 'Rejected',
        borderStyle: 'border-dashed',
      };

    default:
      return {
        borderClass: 'border-primaryLight text-white',
        badgeColor: 'bg-primaryLight text-white',
        badgeIcon: <ClockIcon className="[&>path]:stroke-white [&>circle]:stroke-white mr-2 w-[16px] h-[16px]" />,
        badgeText: 'Verification',
        borderStyle: 'border-dashed',
      };
  }
};

export const DocumentCard: FC<{
  label?: string;
  status?: VerificationFileStatus;
  children: React.ReactNode;
}> = ({ label, status, children }) => {
  const { t } = useAppTranslation('common');
  const statusClass = getStatusClass(status);

  return (
    <div
      className={`relative flex flex-col border-2 ${statusClass.borderStyle} ${statusClass.borderClass}
        rounded-2xl p-4 text-center bg-white transition-all duration-200
        hover:shadow-xl desktop:min-h-[200px]`}
    >
      {status && (
        <div
          className={`absolute -top-3 -right-3 flex items-center gap-1
            text-xs font-semibold px-2 py-1 rounded-full shadow-sm
            z-50 ${statusClass.badgeColor}`}
        >
          {statusClass.badgeIcon}
          <span>{t(statusClass.badgeText)}</span>
        </div>
      )}

      {label && <p className="font-medium text-primaryLight mb-3 text-sm md:text-base">{label}</p>}

      {children}
    </div>
  );
};
